/**
 * One official's photograph, fetched with the credential and rendered from an object URL.
 *
 * ═════════════════════════════════════════════════════════════════════════════
 * WHY A HOOK AND NOT AN `<img src>`
 * ═════════════════════════════════════════════════════════════════════════════
 *
 * Every call to this API carries the device credential in an `Authorization` header, and a
 * browser attaches no header of its own to an image request. `<img src="/api/officials/…">`
 * would therefore arrive anonymously, be refused by the deny-by-default gate, and render as a
 * broken-image glyph on a real official's profile. So the bytes are fetched through the same
 * client as everything else, wrapped in an object URL, and handed to the `<img>` as a `blob:`
 * address the browser can read without asking anybody.
 *
 * ═════════════════════════════════════════════════════════════════════════════
 * THREE STATES, AND WHY THERE IS NO FOURTH
 * ═════════════════════════════════════════════════════════════════════════════
 *
 *   · `null` **and not loading** — there is no photograph, or the fetch failed. The caller
 *     draws its initials placeholder, which is what it drew before photographs existed.
 *   · `null` **and loading** — a photograph is on its way. The caller still draws the
 *     placeholder rather than an empty square, so the card never has a hole in it.
 *   · a `blob:` URL — draw the photograph.
 *
 * **A failure is not a fourth state and gets no error surface.** A photograph that cannot be
 * read is not a finding about the official and not something the Technical Delegate can act
 * on mid-tournament; the honest render is the same initials block the profile has always
 * shown. What must never happen is a broken image, which reads as a fault in the person's
 * record rather than in the fetch.
 *
 * ═════════════════════════════════════════════════════════════════════════════
 * THE SESSION CACHE, AND THE TRADE IT MAKES (RUN 29)
 * ═════════════════════════════════════════════════════════════════════════════
 *
 * Until run 29 one photograph had one consumer — the profile card — and the object URL was
 * revoked on unmount. The roster listing changed the arithmetic: forty-eight rows each hold an
 * avatar, the same official appears on the listing AND on his own profile header, and React
 * re-renders and remounts far more often than a person opens a page. Per-instance fetching
 * would put forty-eight requests on the wire for one screen and forty-eight more the next time
 * a role tab was clicked, on a venue connection, for images that had not changed.
 *
 * So the fetch is keyed on `tmsPersonId` in a module-level cache, shared by every consumer:
 *
 *   · **One request per official per session.** Two avatars for the same person — a roster row
 *     and the profile header — share one in-flight promise and one object URL.
 *   · **Failures are cached too**, as a `null`. A 403 (an Umpires Manager who can read the
 *     roster but not a face) or a 404 (a `photo_ref` whose file is gone) is a stable answer for
 *     the session, and re-asking it forty-eight times per render is the exact behaviour the
 *     cache exists to prevent. The render is the initials circle either way.
 *   · **Nothing is revoked.** An object URL is only safely revoked when no consumer holds it,
 *     and with a shared cache no component can know that — a roster row unmounting while the
 *     profile header still draws the same face would break the header. Revoking is therefore
 *     not attempted at all, and **the blobs live for the life of the document**.
 *
 * **The memory that costs**, stated plainly because it is a deliberate leak: at most one image
 * per official on the panel — around fifty small intake photographs, a few hundred kilobytes in
 * total. That is the price of a listing that does not refetch, and it is bounded by the size of
 * the roster rather than by how long the Control Centre is left open. A reload clears it.
 */

import { useEffect, useState } from "react";

import { fetchOfficialPhoto } from "../../api/client.js";

/**
 * Settled answers: a `blob:` URL, or `null` for "there is no readable photograph". Keyed on
 * `tmsPersonId`, never evicted — see the header for the trade.
 */
const settled = new Map<string, string | null>();

/** Requests in flight, so two consumers mounting together share one fetch rather than two. */
const inFlight = new Map<string, Promise<string | null>>();

/**
 * The one fetch per official. Resolves — it never rejects — because a photograph that cannot be
 * read is not a finding about the official, and every caller's fallback is the same placeholder.
 */
function photoFor(tmsPersonId: string): Promise<string | null> {
  const done = settled.get(tmsPersonId);
  if (done !== undefined) return Promise.resolve(done);

  const pending = inFlight.get(tmsPersonId);
  if (pending !== undefined) return pending;

  const request = fetchOfficialPhoto(tmsPersonId)
    .then((blob) => URL.createObjectURL(blob))
    .catch(() => null)
    .then((url) => {
      settled.set(tmsPersonId, url);
      inFlight.delete(tmsPersonId);
      return url;
    });

  inFlight.set(tmsPersonId, request);
  return request;
}

/**
 * Empties the cache. **For tests only** — one test file's stubbed `fetch` and stubbed
 * `URL.createObjectURL` must not answer the next file's, and module state outlives both.
 */
export function resetOfficialPhotoCache(): void {
  settled.clear();
  inFlight.clear();
}

export interface OfficialPhoto {
  /** A `blob:` URL, or null when there is nothing to draw. */
  url: string | null;
  /** True while a fetch is in flight. The caller keeps its placeholder either way. */
  loading: boolean;
}

/**
 * @param tmsPersonId whose photograph to fetch.
 * @param photoRef the official's stored photograph name, from the profile projection. **Null
 *   means the record carries no photograph, and nothing is fetched at all** — the profile
 *   already knows the answer, and a request that would certainly 404 is a request not worth
 *   making, on a venue connection least of all.
 */
export function useOfficialPhoto(
  tmsPersonId: string,
  photoRef: string | null,
): OfficialPhoto {
  // A cached face is drawn on the FIRST render, not after one. A roster row that flashed its
  // initials on every re-render before settling back to the same photograph would read as a
  // record flickering between two answers.
  const [url, setUrl] = useState<string | null>(() =>
    photoRef === null ? null : (settled.get(tmsPersonId) ?? null),
  );
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (photoRef === null) {
      setUrl(null);
      setLoading(false);
      return;
    }

    const known = settled.get(tmsPersonId);
    if (known !== undefined) {
      setUrl(known);
      setLoading(false);
      return;
    }

    // `cancelled` rather than an AbortController: the request is shared with every other
    // consumer of this official's face, so tearing this component down must not abort it —
    // it must only stop THIS component setting state after it has gone.
    let cancelled = false;
    setUrl(null);
    setLoading(true);

    void photoFor(tmsPersonId).then((resolved) => {
      if (cancelled) return;
      // `photoFor` never rejects: a photograph that would not load resolves as null, which is
      // the placeholder, because it is not a finding about this official. See the header.
      setUrl(resolved);
      setLoading(false);
    });

    return () => {
      cancelled = true;
    };
  }, [tmsPersonId, photoRef]);

  return { url, loading };
}
